import { Fragment, useMemo } from 'react';
import { LinkifiedText } from '../components/LinkifiedText.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { useMembersStore, sessionInfo, type MemberEntry } from '../people/membersStore.js';
import { splitMentions } from '../../messaging/mentions.js';
import { splitEmojis } from '../../messaging/smileys.js';
import { reactionUrl } from '@campus/core';

/** Texte d'un message : liens cliquables, mentions « @Prénom Nom » mises en valeur (la sienne plus fort). */
export function MessageText({ text }: { text: string }) {
  const members = useMembersStore((s) => s.members);
  const me = sessionInfo().userId;
  const parts = useMemo(() => splitMentions(text, [...members.values()]), [text, members]);
  return (
    <>
      {parts.map((p, i) => (p.mention
        ? <span key={i} className={`pl-mention${p.mention.userId === me ? ' is-me' : ''}`}>{p.text}</span>
        : <Fragment key={i}><EmojiText text={p.text} /></Fragment>))}
    </>
  );
}

/** Emojis du catalogue en images Fluent (même rendu partout), le texte copié garde le caractère. */
function EmojiText({ text }: { text: string }) {
  return (
    <>
      {splitEmojis(text).map((p, i) => ('reaction' in p
        ? <img key={i} className="pl-emoji" src={reactionUrl(p.reaction.id)} alt={p.reaction.char} title={p.reaction.label} draggable={false} />
        : <Fragment key={i}><LinkifiedText text={p.text} /></Fragment>))}
    </>
  );
}

/** Suggestions pendant la frappe d'une mention, au-dessus de la saisie. */
export function MentionSuggestions({ items, active, onPick, onHover }: {
  items: MemberEntry[];
  active: number;
  onPick: (m: MemberEntry) => void;
  onHover: (index: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="pl-mention-list" role="listbox" aria-label="Mentionner quelqu'un">
      {items.map((m, i) => (
        <li key={m.userId} role="option" aria-selected={i === active}
          className={i === active ? 'is-active' : undefined}
          // mousedown : la saisie garde le focus.
          onMouseDown={(e) => { e.preventDefault(); onPick(m); }}
          onMouseEnter={() => onHover(i)}>
          <PersonAvatar userId={m.userId} name={m.displayName} size={24} />
          <span>{m.displayName}</span>
        </li>
      ))}
    </ul>
  );
}
